import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid'
import { AgendaContext } from '../context/AgendaContext';
import ToggleForm from './ToggleForm';
import GenericForm from './forms/GenericForm';
import DocActionMenu from './DocActionMenu';
import { useTranslation } from 'react-i18next';

const Table = styled.div`
    display: flex; 
    flex-direction: column; 
    width: 100%;

    // The rows 
    & > div {
        display: flex; 
        width: 100%; 

        // The cells
        & > span {
            display: flex; 
            justify-content: flex-start; 
            margin: 0 4px 0 0; 
        }

        & > span:first-of-type,
        & > span:last-of-type {
            flex: 1;
        }

        & > span:nth-of-type(2) {
            flex: 2; 
        }

        & > span:nth-of-type(3) {
            flex: 3;
        }

        & > span:last-of-type {
            justify-content: flex-end; 
        }
    }

    // The titles in table head 
    & > div:first-of-type {
        & > span {
            font-weight: 600;  
            padding: 0 0 1rem 0; 
        }
    }

    & > div:last-of-type > form {
        flex: auto; 
    }

    & > div:last-of-type > span:first-of-type, 
    & > div:last-of-type > span:nth-of-type(2), {
        width: 100%; 
        white-space: pre-wrap;
        overflow-wrap: anywhere; 
        word-break: break-word; 
    }
`;

const Content = styled.div`
    margin: 0 0 1rem 0;

    ${({ $isLast }) =>
        $isLast &&
        `
        margin-bottom: 0;
    `}

    & > p {
        margin: 0 0 0.5rem 0;
        white-space: pre-line; 
        width: 100%; 
        overflow-wrap: anywhere; 
        word-break: break-word;
    }
`;

const IecClcWgReports = () => {
    const { t } = useTranslation(); 
    const { iecClcWgReports, setIecClcWgReports } = useContext(AgendaContext);

    const dynamicFields = [
        {
            type: 'text',
            label: t('reportsFromIecAndClcWorkingGroups.formLabels.group.name'),
            name: 'group',
            placeholder: t('reportsFromIecAndClcWorkingGroups.formLabels.group.label'),
            required: true,
        },
        {
            type: 'text',
            label: t('reportsFromIecAndClcWorkingGroups.formLabels.representatives.name'),
            name: 'agent',
            placeholder: t('reportsFromIecAndClcWorkingGroups.formLabels.representatives.label'),
            required: true,
        },
        {
            type: 'textarea',
            label: t('reportsFromIecAndClcWorkingGroups.formLabels.activity.note.name'),
            name: 'activity.notes',
            placeholder:  t('reportsFromIecAndClcWorkingGroups.formLabels.activity.note.label'),
            required: true,
            subFields: [
                { type: 'text', label: t('reportsFromIecAndClcWorkingGroups.formLabels.activity.text.name'), name: 'activity.link.text', placeholder: t('reportsFromIecAndClcWorkingGroups.formLabels.activity.text.label'), required: false },
                { type: 'text', label: t('reportsFromIecAndClcWorkingGroups.formLabels.activity.url.name'), name: 'activity.link.url', placeholder: t('reportsFromIecAndClcWorkingGroups.formLabels.activity.url.label'), required: false },
            ]
        },
    ]

    const [editItem, setEditItem] = useState({ id: null, group: '', agent: '', activity: { link: { text: '', url: '' }, notes: '' } });
    const [newTask, setNewTask] = useState({ id: null, group: '', agent: '', activity: { link: { text: '', url: '' }, notes: '' } });

    useEffect(() => {
        localStorage.setItem(
            'reports_IEC_CLC', 
            JSON.stringify(iecClcWgReports ?? [])
        );
    }, [iecClcWgReports]);

    const handleEditCancel = () => {
        setEditItem(prev => ({
            ...prev, 
            id: null
        })); 
    }; 

    const submitEditTask = (event) => {
        event.preventDefault();
        const { group, agent, activity } = editItem;

        setIecClcWgReports((prevItems) =>
            prevItems.map((item) =>
                item.id === editItem.id
                    ? { ...item, group, agent, activity }
                    : item
            )
        );

        // Reset editItem state
        setEditItem({ id: null, group: '', agent: '', activity: { link: { text: '', url: '' }, notes: '' } });
    };

    const submitNewTask = (event) => {
        event.preventDefault();
        const { group, agent, activity } = newTask;

        setIecClcWgReports((prevItems) => [
            ...prevItems,
            {
                id: uuidv4(),
                group: group,
                agent: agent,
                activity: activity,
            }
        ]);

        // Reset newTask state
        setNewTask({ id: null, group: '', agent: '', activity: { link: { text: '', url: '' }, notes: '' } });
    };

    const handleNewTaskChange = (event) => {
        const { name, value } = event.target;
        if (name.startsWith('activity.link.')) {
            const property = name.split('.')[2]; // go two levels in and get activity.link which contains text + url
            setNewTask(prev => ({
                ...prev,
                activity: {
                    ...prev.activity,
                    link: {
                        ...prev.activity.link,
                        [property]: value
                    }
                }
            }));
        } else if (name.startsWith('activity.')) {
            const property = name.split('.')[1]; // go one level in and get activity.notes 
            setNewTask(prev => ({
                ...prev,
                activity: {
                    ...prev.activity,
                    [property]: value
                }
            }));
        } else {
            setNewTask(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleEditItemChange = (event) => {
        const { name, value } = event.target;
        if (name.startsWith('activity.link.')) {
            const property = name.split('.')[2]; // go two levels in and get activity.link which contains text + url
            setEditItem(prev => ({
                ...prev,
                activity: {
                    ...prev.activity,
                    link: {
                        ...prev.activity.link,
                        [property]: value
                    }
                }
            }));
        } else if (name.startsWith('activity.')) {
            const property = name.split('.')[1]; // go one level in and get activity.notes 
            setEditItem(prev => ({
                ...prev,
                activity: {
                    ...prev.activity,
                    [property]: value
                }
            }));
        } else {
            setEditItem(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleEditTaskClick = (item) => {
        setEditItem({
            ...item,
            group: item.group,
            agent: item.agent,
            activity: item.activity,
        });
    };

    const handleDeleteTaskClick = (id) => {
        const deleteItems = iecClcWgReports.filter((item) => item.id !== id);
        setIecClcWgReports(deleteItems);
        localStorage.setItem('reports_IEC_CLC', JSON.stringify(deleteItems));
    };
    return (
        <div>
            {iecClcWgReports && iecClcWgReports.length > 0 ? (
                <Table>
                    <div>
                        <span>{t('reportsFromIecAndClcWorkingGroups.formLabels.group.name')}</span>
                        <span>{t('reportsFromIecAndClcWorkingGroups.formLabels.representatives.name')}</span>
                        <span>{t('reportsFromIecAndClcWorkingGroups.formLabels.activity.title')}</span>
                        <span></span>
                    </div>
                    {iecClcWgReports.map((item, index) => (
                        <div key={item.id}>
                            {editItem.id === item.id ? (
                                <GenericForm
                                    item={editItem}
                                    setItem={setEditItem}
                                    handleOnChange={handleEditItemChange}
                                    handleSubmit={submitEditTask}
                                    handleEditCancel={handleEditCancel}
                                    title={`${t('common.edit')} ${t('reportsFromIecAndClcWorkingGroups.title')} #${index + 1}`}
                                    buttonText='Spara'
                                    dynamicFields={dynamicFields}
                                    buttonColorTheme='lightBlue'
                                    isEditing={editItem.id === item.id}

                                />
                            ) : (
                                <>
                                    <span>{item.group}</span>
                                    <span>{item.agent}</span>
                                    <span>
                                        <Content
                                            $lastItem={index === iecClcWgReports.length - 1}
                                        >
                                            <p>{item?.activity?.notes}</p>
                                            {item.activity.link && (item.activity.link['url'] !== '') && (
                                                <a href={item.activity?.link?.url} target="_blank" rel="noreferrer">
                                                    {item.activity?.link?.text || 'Se länk till aktivitet'}
                                                </a>
                                            )}
                                        </Content>
                                    </span>
                                    <span>
                                        <DocActionMenu
                                            item={item}
                                            handleDeleteText={handleDeleteTaskClick}
                                            handleEditTextClick={handleEditTaskClick}
                                        />
                                    </span>
                                </>
                            )}
                        </div>
                    ))}
                </Table>
            ) : (
                <span>{t('common.noContent')}</span>
            )}
            <ToggleForm>
                <GenericForm
                    item={newTask}
                    setItem={setNewTask}
                    handleOnChange={handleNewTaskChange}
                    handleSubmit={submitNewTask}
                    title={`${t('common.addBtnLabel')} ${t('reportsFromIecAndClcWorkingGroups.title')}`}
                    dynamicFields={dynamicFields}
                    buttonColorTheme='darkBlue'
                />
            </ToggleForm>
        </div>
    );
};

export default IecClcWgReports;